import { useEffect, useRef, useState } from "react";
import classNames from "classnames";
import { useTypedFlags } from "@hooks/useTypedFlags";
import { useActiveWorkspaceRole } from "@hooks/permissions/useActiveWorkspaceRole";
import { UserWorkspaceRole } from "@common/userWorkspaceRoles";
import { useScope } from "@state/scopeContext.lib";
import { useConversations } from "@api/agentChat.api";
import type { AgentChatLiveResponseState } from "@api/agentChatStream.api";
import {
    AgentChatComposer,
    type AgentChatComposerHandle,
} from "./AgentChatComposer";
import { AgentChatSuggestionsPopover } from "./AgentChatSuggestionsPopover";
import { AgentChatThread, type PendingSubmission } from "./AgentChatThread";
import styles from "./AgentChat.module.scss";

type WidgetState =
    | { stage: "collapsed" }
    | { stage: "focused"; conversationId: null }
    | { stage: "thread"; conversationId: string };

export const AgentChatWidget: React.FC = () => {
    const { workspaceId } = useScope();
    const { agentChat } = useTypedFlags();
    // The assistant holds book-mutating tools, so it stays closed to View-only
    // while it is pre-release. Compared against the role rather than a
    // "can modify" helper on purpose: the role reads `null` while the workspace
    // loads, and hiding the widget on every page load is the worse failure.
    const isViewOnly = useActiveWorkspaceRole() === UserWorkspaceRole.VIEW_ONLY;
    const [state, setState] = useState<WidgetState>({ stage: "collapsed" });
    const [draft, setDraft] = useState("");
    const [pendingSubmission, setPendingSubmission] =
        useState<PendingSubmission | null>(null);
    const [liveResponse, setLiveResponse] =
        useState<AgentChatLiveResponseState | null>(null);
    const composerRef = useRef<AgentChatComposerHandle | null>(null);

    const collapseToStart = () => {
        setState({ stage: "collapsed" });
    };

    const anchorRef = useRef<HTMLDivElement | null>(null);

    // Outside-click collapse. We listen on `mousedown` (not `click`) so the
    // containment check runs before React handles the click and unmounts any
    // children — e.g. clicking a recent-conversation row removes the popover
    // from the DOM, which would otherwise make `.contains(event.target)` return
    // false on the subsequent click event and falsely collapse the widget.
    useEffect(() => {
        const handleMouseDown = (event: MouseEvent) => {
            if (!anchorRef.current) {
                return;
            }
            if (anchorRef.current.contains(event.target as Node)) {
                return;
            }
            collapseToStart();
        };
        document.addEventListener("mousedown", handleMouseDown);
        return () => {
            document.removeEventListener("mousedown", handleMouseDown);
        };
    }, []);

    // Reset to collapsed whenever the active workspace switches — prevents a
    // conversationId from workspace A leaking into workspace B (the backend would
    // 404, but resetting locally is cleaner and saves the round-trip).
    useEffect(() => {
        setState({ stage: "collapsed" });
        setDraft("");
        setPendingSubmission(null);
        setLiveResponse(null);
    }, [workspaceId]);

    const isActive = state.stage !== "collapsed";

    const conversations = useConversations({
        workspaceId: workspaceId ?? "",
        limit: 10,
        enabled: Boolean(workspaceId) && agentChat && isActive,
    });
    const historyCount = conversations.data?.pages[0]?.totalCount ?? null;

    if (!agentChat || !workspaceId || isViewOnly) {
        return null;
    }

    const focusComposerWithDraft = (text: string) => {
        setDraft(text);
        composerRef.current?.focus();
    };

    return (
        <div
            ref={anchorRef}
            className={classNames(
                styles.widgetAnchor,
                isActive
                    ? styles.widgetAnchorExpanded
                    : styles.widgetAnchorCollapsed,
            )}
        >
            {state.stage === "focused" && !pendingSubmission && (
                <AgentChatSuggestionsPopover
                    workspaceId={workspaceId}
                    onPromptSelected={focusComposerWithDraft}
                    onHistorySelected={(conversationId) =>
                        setState({ stage: "thread", conversationId })
                    }
                />
            )}
            {(state.stage === "thread" ||
                (state.stage === "focused" &&
                    (pendingSubmission ?? liveResponse))) && (
                <AgentChatThread
                    conversationId={
                        state.stage === "thread" ? state.conversationId : null
                    }
                    workspaceId={workspaceId}
                    onFollowUpSelected={focusComposerWithDraft}
                    pendingSubmission={pendingSubmission}
                    liveResponse={liveResponse}
                />
            )}
            <AgentChatComposer
                key={workspaceId}
                ref={composerRef}
                workspaceId={workspaceId}
                conversationId={
                    state.stage === "thread" ? state.conversationId : null
                }
                draft={draft}
                onDraftChange={setDraft}
                historyCount={historyCount}
                onFocus={() => {
                    if (state.stage === "collapsed") {
                        setState({ stage: "focused", conversationId: null });
                    }
                }}
                onCollapse={collapseToStart}
                onClose={collapseToStart}
                onSent={(conversationId) =>
                    setState({ stage: "thread", conversationId })
                }
                onPendingChange={setPendingSubmission}
                onLiveResponseChange={setLiveResponse}
            />
        </div>
    );
};
